import React from 'react'
import { motion } from 'framer-motion'
import { GeneratedSlide } from '../types/generator'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

interface SlidePreviewProps {
  slides: GeneratedSlide[]
  onClose: () => void
  onStartPresentation: (slides: GeneratedSlide[]) => void
}

const SlidePreview: React.FC<SlidePreviewProps> = ({ slides, onClose, onStartPresentation }) => {
  const [currentIndex, setCurrentIndex] = React.useState(0)

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const currentSlide = slides[currentIndex]

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6">
      <div className="w-full max-w-5xl h-full max-h-[95vh] flex flex-col bg-slate-900 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 md:p-5 border-b border-slate-700/50 bg-slate-800/50">
          <h2 className="text-base sm:text-lg md:text-xl font-bold text-white">Preview Slides</h2>
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <span className="text-xs sm:text-sm text-gray-400">
              {currentIndex + 1} of {slides.length}
            </span>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded transition-colors text-gray-300 hover:text-white"
              aria-label="Close preview"
            >
              <X size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Slide Display */}
        <div className="flex-1 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-auto">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="min-h-full flex flex-col items-center justify-center text-center p-6 sm:p-8 md:p-10"
          >
            {currentSlide.type === 'title' && (
              <>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-bible-gold mb-3 sm:mb-4">{currentSlide.title}</h1>
                {currentSlide.subtitle && (
                  <h2 className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-4 sm:mb-6">{currentSlide.subtitle}</h2>
                )}
                {currentSlide.content && (
                  <p className="text-sm sm:text-base md:text-lg text-gray-400 mb-6 sm:mb-8 max-w-3xl mx-auto">{currentSlide.content}</p>
                )}
                {currentSlide.points && (
                  <ul className="text-left space-y-2 text-sm sm:text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
                    {currentSlide.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-bible-gold mt-1">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {currentSlide.type === 'verse' && (
              <>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-bible-gold mb-4 sm:mb-6">{currentSlide.title}</h2>
                {currentSlide.verse && (
                  <div className="bg-slate-800/50 rounded-lg p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 max-w-4xl mx-auto">
                    <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-200 italic mb-3 sm:mb-4">
                      "{currentSlide.verse}"
                    </p>
                    {currentSlide.verseReference && (
                      <p className="text-xs sm:text-sm md:text-base text-bible-gold">{currentSlide.verseReference}</p>
                    )}
                  </div>
                )}
              </>
            )}

            {(currentSlide.type === 'explanation' || currentSlide.type === 'conclusion') && (
              <>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-bible-gold mb-4 sm:mb-6">{currentSlide.title}</h2>
                {currentSlide.content && (
                  <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto">{currentSlide.content}</p>
                )}
                {currentSlide.points && (
                  <ul className="text-left space-y-2 sm:space-y-3 text-sm sm:text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
                    {currentSlide.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 sm:gap-3">
                        <span className="text-bible-gold mt-1">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </motion.div>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-t border-slate-700/50 bg-slate-800/30">
          <button
            onClick={prevSlide}
            disabled={currentIndex === 0}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-bible-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} className="sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </button>

          <div className="flex gap-1.5 sm:gap-2 flex-wrap justify-center max-w-xs sm:max-w-none">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-bible-gold' : 'bg-gray-600 hover:bg-gray-500'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentIndex === slides.length - 1}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-bible-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight size={16} className="sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 p-3 sm:p-4 md:p-5 border-t border-slate-700/50 bg-slate-800/30">
          <button
            onClick={onClose}
            className="flex-1 px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onStartPresentation(slides)}
            className="flex-1 px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-bible-gold text-slate-900 rounded-lg hover:bg-yellow-500 font-semibold transition-colors"
          >
            Start Presentation
          </button>
        </div>
      </div>
    </div>
  )
}

export default SlidePreview

